import { useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { useBlocker, useLocation } from 'react-router-dom';
import { NOTIFICATION_VARIANT, useNotifications } from '../components/notifications/Notifications';

interface UseUnsavedChangesGuardArgs {
    shouldWarn: boolean;
    allowNextNavigationRef?: MutableRefObject<boolean>;
    onWarning?: () => void;
    includeSearchAndHash?: boolean;
    confirmationTimeout?: number;
    warnOnPageUnload?: boolean;
}

export function useUnsavedChangesGuard({
    shouldWarn,
    allowNextNavigationRef: externalAllowNextNavigationRef,
    onWarning,
    includeSearchAndHash = true,
    confirmationTimeout = 5000,
    warnOnPageUnload = false,
}: UseUnsavedChangesGuardArgs): void {
    const { addNotification } = useNotifications();
    const location = useLocation();
    const internalAllowNextNavigationRef = useRef(false);
    const allowNextNavigationRef = externalAllowNextNavigationRef ?? internalAllowNextNavigationRef;
    const hasBlockedNavigationAttemptRef = useRef(false);
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const removeWarningRef = useRef<(() => void) | null>(null);

    const resetConfirmation = useCallback(() => {
        hasBlockedNavigationAttemptRef.current = false;
        if (resetTimerRef.current) {
            clearTimeout(resetTimerRef.current);
            resetTimerRef.current = null;
        }
        removeWarningRef.current?.();
        removeWarningRef.current = null;
    }, []);

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (!shouldWarn || allowNextNavigationRef.current) {
            return false;
        }

        return (
            currentLocation.pathname !== nextLocation.pathname ||
            (includeSearchAndHash &&
                (currentLocation.search !== nextLocation.search ||
                    currentLocation.hash !== nextLocation.hash))
        );
    });

    useEffect(() => {
        allowNextNavigationRef.current = false;
    }, [allowNextNavigationRef, location.key]);

    const guardedSearch = includeSearchAndHash ? location.search : '';
    const guardedHash = includeSearchAndHash ? location.hash : '';

    useEffect(() => {
        resetConfirmation();
    }, [location.pathname, guardedSearch, guardedHash, resetConfirmation]);

    useEffect(() => {
        if (!warnOnPageUnload || !shouldWarn) {
            return undefined;
        }

        // useBlocker cannot catch page unloads; browsers only allow their native dialog here.
        function handleBeforeUnload(event: BeforeUnloadEvent) {
            event.preventDefault();
            event.returnValue = '';
        }
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [shouldWarn, warnOnPageUnload]);

    useEffect(() => resetConfirmation, [resetConfirmation]);

    useEffect(() => {
        if (!shouldWarn) {
            resetConfirmation();
        }
    }, [shouldWarn, resetConfirmation]);

    useEffect(() => {
        if (blocker.state !== 'blocked') {
            return;
        }

        if (hasBlockedNavigationAttemptRef.current) {
            allowNextNavigationRef.current = true;
            resetConfirmation();
            blocker.proceed();
            return;
        }

        resetConfirmation();
        onWarning?.();
        removeWarningRef.current = addNotification({
            message: gettext('UNSAVED_CHANGES_CLOSE_AGAIN'),
            variant: NOTIFICATION_VARIANT.WARNING,
        }).remove;
        hasBlockedNavigationAttemptRef.current = true;
        blocker.reset();

        resetTimerRef.current = setTimeout(resetConfirmation, confirmationTimeout);
    }, [
        addNotification,
        allowNextNavigationRef,
        blocker,
        confirmationTimeout,
        onWarning,
        resetConfirmation,
    ]);
}
